import type { SoundCategory } from "./SoundCategory.js";
import type { SoundDefinition } from "./SoundDefinition.js";

/**
 * 정적 사운드 정의 컬렉션. id로 lookup + 그룹 필터.
 *
 * SSoT는 `data/tsv/sounds.tsv` → `npm run export:data`로 변환된 JSON.
 * 앱 부트 시 fetch → `SoundCatalog.fromDefinitions(json)`.
 *
 * id 중복은 생성 시 throw — 사일런트 덮어쓰기 방지 (TSV export 단계도 같은 검증을 하지만
 * 외부 데이터 직접 주입 케이스도 안전하게).
 */
export class SoundCatalog {
  private readonly map: ReadonlyMap<string, SoundDefinition>;

  private constructor(map: ReadonlyMap<string, SoundDefinition>) {
    this.map = map;
  }

  static fromDefinitions(defs: readonly SoundDefinition[]): SoundCatalog {
    const map = new Map<string, SoundDefinition>();
    for (const def of defs) {
      if (map.has(def.id)) {
        throw new Error(`[SoundCatalog] duplicate sound id: ${def.id}`);
      }
      map.set(def.id, def);
    }
    return new SoundCatalog(map);
  }

  get(id: string): SoundDefinition | undefined {
    return this.map.get(id);
  }

  has(id: string): boolean {
    return this.map.has(id);
  }

  byCategory(category: SoundCategory): SoundDefinition[] {
    return this.all().filter((d) => d.category === category);
  }

  all(): SoundDefinition[] {
    return Array.from(this.map.values());
  }

  size(): number {
    return this.map.size;
  }
}
